/* eslint-disable react/no-unstable-nested-components */
import type { ColumnDefinition } from 'components/SubTable';
import type { InvoiceLineItemType } from 'payments-api/model/invoiceLineItemType';
import { InvoiceLineItemType as LineItemType } from 'payments-api/model/invoiceLineItemType';
import React, { useMemo } from 'react';

import { Badge } from '@fragment/ui/src/components/Badge/Badge';
import { Icon } from '@fragment/ui/src/components/Icon/BaseIcon/Icon';
import { CurrencyField } from '@fragment/ui/src/components/Payments/CurrencyField';
import {
  LayoutRow,
  TextCell,
} from '@fragment/ui/src/components/Payments/LayoutRow';

export type Obligation = {
  id: string;
  name: string;
  type: InvoiceLineItemType;
  amount: number;
  paid: number;
  dueDate?: string;
  isStrikethrough?: boolean;
  isDisputed?: boolean;
};

type Props = {
  obligations: {
    payin: Obligation[];
    payout: Obligation[];
  };
  onObligationClick?: (obligation: Obligation) => void;
  isAdjusting?: boolean;
};

export const OverviewPane = ({
  obligations,
  onObligationClick,
  isAdjusting,
}: Props): JSX.Element => {
  // Define schema for obligations table
  const obligationsSchema: readonly ColumnDefinition<Obligation>[] = useMemo(
    () => [
      {
        title: 'Name',
        format: (obligation: Obligation) => obligation.name,
      },
      {
        title: 'Paid',
        justify: 'right' as const,
        format: (obligation: Obligation) => {
          if (obligation.isStrikethrough) {
            return (
              <CurrencyField
                amount={obligation.paid}
                currencyPrecision={2}
                currencyCode="USD"
                textSize="md"
                align="right"
                paid
              />
            );
          }

          // Determine which icon to show based on due date and payment status
          let icon: React.ReactElement | null = null;
          if (obligation.dueDate) {
            const today = new Date().toISOString().split('T')[0];
            const isPastDue = obligation.dueDate < today;
            const isFullyPaid = obligation.paid === obligation.amount;

            // For payouts, only show alert if more than 2 days past due
            if (obligation.type === LineItemType.payout) {
              if (!isFullyPaid && obligation.paid === 0) {
                // Calculate if current date is more than 2 days past the due date
                const dueDateTime = new Date(obligation.dueDate).getTime();
                const todayTime = new Date(today).getTime();
                const twoDaysInMs = 2 * 24 * 60 * 60 * 1000;
                const isMoreThan2DaysPastDue =
                  todayTime - dueDateTime > twoDaysInMs;

                if (isMoreThan2DaysPastDue) {
                  // Alert icon if more than 2 days past due and not fully paid
                  icon = (
                    <Icon type="alert" size="sm" backgroundColor="orange" />
                  );
                }
              } else if (isFullyPaid) {
                // Success icon if paid equals amount and due date is today or future
                icon = (
                  <Icon
                    type="success"
                    size="sm"
                    backgroundColor="asset-green"
                  />
                );
              }
            } else {
              // For payins (and other types), use original logic
              if (isPastDue && (obligation.paid === 0 || !isFullyPaid)) {
                // Alert icon if paid is 0 and due date is past, OR paid doesn't equal amount and due date is past
                icon = <Icon type="alert" size="sm" backgroundColor="orange" />;
              }
              if (isFullyPaid) {
                // Success icon if paid equals amount and due date is today or future
                icon = (
                  <Icon
                    type="success"
                    size="sm"
                    backgroundColor="asset-green"
                  />
                );
              }
            }
            // No icon if due date is today or future and paid doesn't equal amount
          }

          if (icon) {
            return (
              <Badge badge={icon} direction="rtl">
                <CurrencyField
                  amount={obligation.paid}
                  currencyPrecision={2}
                  currencyCode="USD"
                  textSize="md"
                  align="right"
                />
              </Badge>
            );
          }

          return (
            <CurrencyField
              amount={obligation.paid}
              currencyPrecision={2}
              currencyCode="USD"
              textSize="md"
              align="right"
            />
          );
        },
      },
      {
        title: 'Amount',
        justify: 'right' as const,
        format: (obligation: Obligation) => (
          <CurrencyField
            amount={obligation.amount}
            currencyPrecision={2}
            currencyCode="USD"
            paid={obligation.isStrikethrough}
            textSize="md"
            align="right"
          />
        ),
      },
    ],
    []
  );

  const handleObligationClick = (obligation: Obligation) => {
    if (onObligationClick) {
      onObligationClick(obligation);
    }
  };

  const RowLayout = ({ children }: { children: React.ReactNode }) => (
    <div className="grid grid-cols-4 w-full h-f4 px-f1 min-h-f4">
      {children}
    </div>
  );

  return (
    <LayoutRow height="xl" justify="start" hide={isAdjusting}>
      <TextCell
        text="Overview"
        textSize="md"
        align="left"
        border="right"
        secondary
      />
      <div className="flex flex-col w-full h-full pt-f1 overflow-y-scroll scrollbar-hide">
        <RowLayout>
          <span />
          <span>Name</span>
          <span>Paid</span>
          <span className="text-right">Amount</span>
        </RowLayout>
        {obligations.payin.map((obligation) => (
          <RowLayout key={obligation.id}>
            <div className="flex items-center w-full h-fit text-md text-main-500">
              <Icon type="down" primaryColor="asset-green" size="md" />
              Buyer
            </div>
            <span>{obligation.name}</span>
            <CurrencyField
              amount={obligation.paid}
              currencyPrecision={2}
              currencyCode="USD"
              textSize="md"
              align="left"
            />
            <CurrencyField
              amount={obligation.amount}
              currencyPrecision={2}
              currencyCode="USD"
              textSize="md"
              align="right"
            />
          </RowLayout>
        ))}

        {obligations.payout.map((obligation) => (
          <RowLayout key={obligation.id}>
            <div className="flex items-center w-full h-fit text-md text-main-500">
              <Icon type="up" primaryColor="liability-red" size="md" />
              Seller
            </div>
            <span>{obligation.name}</span>
            <CurrencyField
              amount={obligation.paid}
              currencyPrecision={2}
              currencyCode="USD"
              textSize="md"
              align="left"
            />
            <CurrencyField
              amount={obligation.amount}
              currencyPrecision={2}
              currencyCode="USD"
              textSize="md"
              align="right"
            />
          </RowLayout>
        ))}
      </div>
    </LayoutRow>
  );
};
